import {Registry} from "@cosmjs/proto-signing";
import {defaultRegistryTypes, SigningStargateClient} from "@cosmjs/stargate";
import {Comet38Client} from "@cosmjs/tendermint-rpc";
// noinspection ES6PreferShortImport
import {msgTypes} from "../ts/structs.structs/registry";
import {AMBIT_ENUM} from "../constants/Ambits";
import {LOCATION_TYPE_INDEX} from "../constants/LocationTypes";
import {PLAYER_TYPES} from "../constants/PlayerTypes";
import {STALE_BLOCK_MS} from "../constants/GrassConstants";
import {SigningQueueManager} from "./SigningQueueManager";
import {ChainQueryClient} from "../api/ChainQueryClient";
import {OwnedRpcClient} from "../api/OwnedRpcClient";
import {validateSigningSnapshot} from "../util/ChainProtocol";
import {SigningSubmission} from "./SigningSubmission";

/** Floor between unforced probes, so an idle player barely touches the node. */
const PROBE_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Thin transport + cosmos message catalog. Owns the cosmjs Registry, the WS
 * endpoint, and the signing client lifecycle. All queueing / scheduling /
 * persistence lives in {@link SigningQueueManager} (this.queue).
 *
 * Each queueMsg* builds a plain, JSON-serializable payload (NO `creator` — it
 * is injected at broadcast time from the transaction's accountAddress) and
 * delegates to enqueueImmediate / enqueueAction. The returned Promise resolves
 * when the transaction reaches a terminal state (succeeded / dropped /
 * cancelled); callers must check `tx.status` before acting on success.
 */
export class SigningClientManager {

  /**
   * @param {GameState} gameState
   * @param {boolean} publicEndpoint
   * @param {string|null} wsUrl Explicit transport endpoint for an isolated client instance.
   */
  constructor(gameState, publicEndpoint = false, wsUrl = null) {
    console.info('Initiating Signing Client Manager');
    this.gameState = gameState;

    // TODO Make this value more dynamic
    // Possibly a database setting or env, provided via server
    //this.wsUrl = `ws://${window.location.hostname}:26657`;
    this.publicEndpoint = publicEndpoint;
    this.wsUrl = wsUrl ?? (this.publicEndpoint
        ? `wss://public.testnet.structs.network:26657`
        : `ws://${window.location.hostname}:26657`);
    console.info('[SigningClientManager] Endpoint ' + this.wsUrl);

    this.registry = new Registry([...defaultRegistryTypes, ...msgTypes]);

    /** @type {number} When the transport was last proven alive. */
    this.lastProbeAt = 0;

    /** @type {?Promise<boolean>} The single in-flight connection check, if any. */
    this.connectionCheck = null;
    this.readyForResume = false;

    this.connectionEpoch = 0;
    this.sessionController = new AbortController();
    this.chainQuery = null;
    this.pendingRpc = null;

    this.queue = new SigningQueueManager(this.gameState, {
      registry: this.registry,
      wsUrl: this.wsUrl,
      submission: new SigningSubmission(this),
    });
  }

  /**
   * @return {Promise<void>}
   */
  async initSigningClient() {
    console.debug("Initializing signing client...");
    this.readyForResume = false;
    this.sessionController.abort();
    this.sessionController = new AbortController();
    // Wallet/account initialization precedes this call. Load evidence even if
    // the node is temporarily unreachable; a failed connect cannot hide it.
    this.queue.loadPersistedState();
    await this.connect();
    this.queue.suspended = false;
    this.readyForResume = true;
    console.info("Signing client initialized.");
    this.queue.reconcilePending()
      .catch(error => console.warn('[SigningClientManager] reconciliation unavailable:', error));
  }

  /**
   * Establish a fresh client, closing any existing one first.
   *
   * @return {Promise<void>}
   */
  async connect() {
    this.disconnect();
    const epoch = this.connectionEpoch;
    const wallet = this.gameState.wallet;
    // Own the socket before the first await, including failed/overlapping connects.
    const rpc = new OwnedRpcClient(this.wsUrl);
    this.pendingRpc = rpc;
    try {
      const comet = await Comet38Client.create(rpc);
      if (epoch !== this.connectionEpoch) throw new Error('Signing connection superseded');
      const query = new ChainQueryClient(comet);
      this.chainQuery = query;
      this.pendingRpc = null;
      await query.status();
      const client = await SigningStargateClient.createWithSigner(comet, wallet, {registry: this.registry});
      if (epoch !== this.connectionEpoch || wallet !== this.gameState.wallet) {
        throw new Error('Signing connection superseded');
      }
      this.gameState.signingClient = client;
      this.lastProbeAt = Date.now();
    } catch (error) {
      if (epoch === this.connectionEpoch) this.disconnect();
      else rpc.disconnect();
      throw error;
    }
  }

  /**
   * Close the current client. Every WS-backed client holds a socket for as long
   * as it lives, so replacing one without closing it leaks that socket; enough
   * of those exhaust the browser's pool and wedge all later signing.
   */
  disconnect() {
    const client = this.gameState.signingClient;
    const query = this.chainQuery;
    const pendingRpc = this.pendingRpc;
    this.connectionEpoch++;
    this.gameState.signingClient = null;
    this.chainQuery = null;
    this.pendingRpc = null;
    this.lastProbeAt = 0;

    try {
      // Query and signer share one socket. Close its owner only once.
      if (query) query.disconnect();
      else if (pendingRpc) pendingRpc.disconnect();
      else if (client) client.disconnect();
    } catch (error) {
      console.warn('[SigningClientManager] error closing previous client:', error);
    }
  }

  /**
   * Stop signing and close the connection; the queue stays suspended until the next init.
   */
  stopForLogout() {
    this.readyForResume = false;
    this.sessionController.abort();
    this.queue.suspended = true;
    this.disconnect();
  }

  /**
   * @return {AbortSignal} aborted when the signing session is reinitialised or logged out
   */
  get sessionSignal() {
    return this.sessionController.signal;
  }

  /**
   * A dead WebSocket goes on reporting readyState OPEN, so the only answer worth
   * trusting comes from a round trip that has to complete.
   *
   * @return {Promise<boolean>}
   */
  async isAlive() {
    const client = this.gameState.signingClient;
    const query = this.chainQuery;
    if (!client || !query) {
      return false;
    }
    try {
      // A fresh, bounded status call checks chain identity as well as liveness.
      await query.status();
      if (query !== this.chainQuery || client !== this.gameState.signingClient) return false;
      this.lastProbeAt = Date.now();
      return true;
    } catch (error) {
      console.warn('[SigningClientManager] liveness probe failed:', error);
      return false;
    }
  }

  /**
   * Validate the exact API response used by an action against a fresh node read.
   *
   * @param {object} meta
   * @param {string[]} requiredModels
   * @return {Promise<object>}
   */
  async getSigningContext(meta, requiredModels = []) {
    const query = this.chainQuery;
    const client = this.gameState.signingClient;
    if (!query || !client) throw new Error('Signing connection unavailable');
    const observed = await query.status();
    if (query !== this.chainQuery || client !== this.gameState.signingClient) {
      throw new Error('Signing connection superseded');
    }
    return validateSigningSnapshot(meta, observed, requiredModels);
  }

  /**
   * Probe the signing transport and rebuild it if it has died. Concurrent calls
   * share one check.
   *
   * @param {boolean} force Probe even if one ran recently. Used by resume triggers.
   * @return {Promise<boolean>} whether a usable client is in place
   */
  resumeCheck(force = false) {
    // Startup owns the first connection. Resume probes must not supersede its
    // in-flight socket or restart signing after a rejected/closed session.
    if (!this.readyForResume || this.queue.suspended) return Promise.resolve(false);
    if (!this.connectionCheck) {
      this.connectionCheck = this.checkConnection(force)
        .then(async alive => {
          if (alive) await this.queue.reconcilePending();
          return alive;
        })
        .catch((error) => {
          console.warn('[SigningClientManager] connection check failed:', error);
          return false;
        })
        .finally(() => {
          this.connectionCheck = null;
        });
    }

    return this.connectionCheck;
  }

  /**
   * @param {boolean} force
   * @return {Promise<boolean>}
   */
  async checkConnection(force) {
    const epoch = this.connectionEpoch;
    if (!this.gameState.wallet) {
      return false;
    }

    if (
      !force
      && this.gameState.signingClient
      && this.chainQuery
      && !this.chainQuery.closed
      && Date.now() - this.lastProbeAt < PROBE_INTERVAL_MS
    ) {
      return true;
    }

    const alive = await this.isAlive();
    if (epoch !== this.connectionEpoch || !this.readyForResume || this.queue.suspended) return false;
    if (alive) {
      return true;
    }

    // The probe cannot tell a dead socket from an unreachable node. Blocks reach
    // us over a different service, so their arrival is what proves the network
    // is fine and isolates the fault to this transport. Rebuilding during a
    // wider outage would just churn sockets.
    if (this.gameState.msSinceLastBlock() >= STALE_BLOCK_MS && !this.queue.hasUnresolvedSubmissions()) {
      return false;
    }

    console.warn('[SigningClientManager] transport is dead, rebuilding.');

    try {
      await this.connect();
      console.info('[SigningClientManager] transport rebuilt.');
      return true;
    } catch (error) {
      // Left disconnected on purpose: a later check retries, and broadcasting
      // through a half-built client would be worse than skipping a block.
      console.error('[SigningClientManager] rebuild failed:', error);
      return false;
    }
  }

  // ---------------------------------------------------------------------------
  // Mutation API — thin delegates so injection sites keep using signingClientManager
  // ---------------------------------------------------------------------------

  /**
   * @param {string} id
   * @return {Promise<boolean>}
   */
  cancelQueueItem(id) {
    return this.queue.cancelQueueItem(id);
  }

  /**
   * @param {string} id
   * @param {number} newIndex
   * @return {boolean}
   */
  reorderActionQueue(id, newIndex) {
    return this.queue.reorderActionQueue(id, newIndex);
  }

  /**
   * @param {string} id
   * @return {boolean}
   */
  moveActionItemUp(id) {
    return this.queue.moveActionItemUp(id);
  }

  /**
   * @param {string} id
   * @return {boolean}
   */
  moveActionItemDown(id) {
    return this.queue.moveActionItemDown(id);
  }

  /**
   * @param {string} id
   * @return {import('../models/SigningTransaction').SigningTransaction|null}
   */
  getTransaction(id) {
    return this.queue.getTransaction(id);
  }

  /**
   * @return {boolean}
   */
  hasQueuedTransactions() {
    return this.queue.hasQueuedTransactions();
  }

  // ---------------------------------------------------------------------------
  // Immediate-lane messages (no charge drain)
  // ---------------------------------------------------------------------------

  /**
   * The observer may leave the screen; that never cancels or replaces the intent.
   *
   * @param {object} intent
   * @param {AbortSignal} [signal] stops waiting, not the transaction
   * @return {Promise<SigningTransaction>}
   */
  async queueTransfer(intent, signal = undefined) {
    signal?.throwIfAborted();
    const id = this.queue.enqueueTransfer(intent);
    return this.queue.whenSettled(id, signal);
  }

  /**
   * @param {object} intent
   * @param {AbortSignal} [signal] stops waiting, not the transaction
   * @return {Promise<SigningTransaction>}
   */
  async queueReactor(intent, signal = undefined) {
    signal?.throwIfAborted();
    const id = this.queue.enqueueReactor(intent);
    return this.queue.whenSettled(id, signal);
  }

  /**
   * @param {object} intent
   * @param {AbortSignal} [signal] stops waiting, not the transaction
   * @return {Promise<SigningTransaction>}
   */
  async queueGenerator(intent, signal = undefined) {
    signal?.throwIfAborted();
    const id = this.queue.enqueueGenerator(intent);
    return this.queue.whenSettled(id, signal);
  }

  /**
   * @param {string} fromAddress
   * @param {string} toAddress
   * @param {Array<{denom: string, amount: string}>} amount
   * @param {object} [options]
   */
  async queueMsgPlayerSend(fromAddress, toAddress, amount, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgPlayerSend',
      {fromAddress, toAddress, amount},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * Owner-to-owner transfer; protocol and spendability are checked before enqueue.
   *
   * @param {string} ownerId
   * @param {string} recipientOwnerId
   * @param {{denom: string, amount: string}} amount
   * @param {object} [options]
   */
  async queueMsgVaultSend(ownerId, recipientOwnerId, amount, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgVaultSend',
      {ownerId, recipientOwnerId, amount},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} playerId
   * @param {string} [name='']
   * @param {object} [options]
   */
  async queueMsgPlanetExplore(playerId, name = '', options = {}) {
    const id = this.queue.enqueueGameplayAction(
      '/structs.structs.MsgPlanetExplore',
      {playerId, name},
      0,
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} playerId
   * @param {string} addressToRegister
   * @param {string} proofPubKey
   * @param {string} proofSignature
   * @param {string|number} permissions
   * @param {object} [options]
   */
  async queueMsgAddressRegister(playerId, addressToRegister, proofPubKey, proofSignature, permissions, options = {}) {
    if (typeof permissions === 'number' && (!Number.isSafeInteger(permissions) || permissions < 0)) {
      throw new Error('Invalid registration permissions');
    }
    const id = this.queue.enqueueRegistration({
      kind: 'address_register',
      version: 1,
      ownerId: playerId,
      address: addressToRegister,
      pubkey: proofPubKey,
      signature: proofSignature,
      permissions: String(permissions),
      proof: options.proofContext,
    });
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} addressToRevoke
   * @param {object} [options]
   */
  async queueMsgAddressRevoke(addressToRevoke, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgAddressRevoke',
      {address: addressToRevoke},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} fleetId
   * @param {string} destinationLocationId
   * @param {object} [options]
   */
  async queueMsgFleetMove(fleetId, destinationLocationId, options = {}) {
    const id = this.queue.enqueueGameplayAction(
      '/structs.structs.MsgFleetMove',
      {fleetId, destinationLocationId},
      0,
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} fleetId
   * @param {string} proof
   * @param {string} nonce
   * @param {object} [options]
   */
  async queueMsgPlanetRaidComplete(fleetId, proof, nonce, options = {}) {
    const id = this.queue.enqueueGameplayImmediate(
      '/structs.structs.MsgPlanetRaidComplete',
      {fleetId, proof, nonce},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} planetId
   * @param {string} name
   * @param {object} [options]
   */
  async queueMsgPlanetUpdateName(planetId, name, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgPlanetUpdateName',
      {planetId, name},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} structId
   * @param {string} proof
   * @param {string} nonce
   * @param {object} [options]
   */
  async queueMsgStructBuildComplete(structId, proof, nonce, options = {}) {
    const id = this.queue.enqueueGameplayImmediate(
      '/structs.structs.MsgStructBuildComplete',
      {structId, proof, nonce},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} structId
   * @param {string} proof
   * @param {string} nonce
   * @param {object} [options]
   */
  async queueMsgStructOreMinerComplete(structId, proof, nonce, options = {}) {
    const id = this.queue.enqueueGameplayImmediate(
      '/structs.structs.MsgStructOreMinerComplete',
      {structId, proof, nonce},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} structId
   * @param {string} proof
   * @param {string} nonce
   * @param {object} [options]
   */
  async queueMsgStructOreRefineryComplete(structId, proof, nonce, options = {}) {
    const id = this.queue.enqueueGameplayImmediate(
      '/structs.structs.MsgStructOreRefineryComplete',
      {structId, proof, nonce},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} controller
   * @param {string} sourceObjectId
   * @param {string} allocationType
   * @param {string|number} power
   * @param {object} [options]
   */
  async queueMsgAllocationCreate(controller, sourceObjectId, allocationType, power, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgAllocationCreate',
      {controller, sourceObjectId, allocationType, power},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} allocationId
   * @param {object} [options]
   */
  async queueMsgAllocationDelete(allocationId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgAllocationDelete',
      {allocationId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} allocationId
   * @param {string|number} power
   * @param {object} [options]
   */
  async queueMsgAllocationUpdate(allocationId, power, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgAllocationUpdate',
      {allocationId, power},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} allocationId
   * @param {string} controller
   * @param {object} [options]
   */
  async queueMsgAllocationTransfer(allocationId, controller, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgAllocationTransfer',
      {allocationId, controller},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string|number} amountAlpha
   * @param {string|number} amountToken
   * @param {string} [guildId] Defaults to the signer's own guild.
   * @param {object} [options]
   */
  async queueMsgGuildBankMint(amountAlpha, amountToken, guildId = '', options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildBankMint',
      {amountAlpha, amountToken, guildId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {{denom: string, amount: string}} amountToken
   * @param {string|number} minAmountAlpha Required and nonzero; the transaction fails
   *   rather than burning tokens when fees or the collateral ratio would return
   *   less alpha.
   * @param {object} [options]
   */
  async queueMsgGuildBankRedeem(amountToken, minAmountAlpha, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildBankRedeem',
      {amountToken, minAmountAlpha},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} address
   * @param {string|number} amountToken
   * @param {string} [guildId] Defaults to the signer's own guild.
   * @param {object} [options]
   */
  async queueMsgGuildBankConfiscateAndBurn(address, amountToken, guildId = '', options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildBankConfiscateAndBurn',
      {address, amountToken, guildId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * Converts ualpha into a guild's alpha-backed token at the current collateral
   * ratio. The guild's bankConvertInFee is retained in the collateral pool.
   *
   * @param {string} guildId
   * @param {string|number} amountAlpha
   * @param {string|number} minAmountToken Required and nonzero; guards against ratio or
   *   fee movement earlier in the block.
   * @param {object} [options]
   */
  async queueMsgGuildBankConvert(guildId, amountAlpha, minAmountToken, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildBankConvert',
      {guildId, amountAlpha, minAmountToken},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * Converts one guild token into another in a single atomic transaction. The
   * source guild is identified by the amountToken denom (uguild.{guildId});
   * guildId names the target.
   *
   * @param {{denom: string, amount: string}} amountToken
   * @param {string} guildId The target guild.
   * @param {string|number} minAmountToken Required and nonzero; guards the final
   *   target-token output.
   * @param {object} [options]
   */
  async queueMsgGuildBankConvertToken(amountToken, guildId, minAmountToken, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildBankConvertToken',
      {amountToken, guildId, minAmountToken},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * Founds a guild, either by solving the chain-global charter proof-of-work
   * (proof + nonce) or by spending a reactor's one-time entitlement (both
   * empty). The signer is the solver; founderPlayerId is who ends up owning the
   * guild, and defaults to the signer when empty. The consent fields are only
   * needed when founding on behalf of another player.
   *
   * @param {string} reactorId
   * @param {string} endpoint
   * @param {string} entrySubstationId
   * @param {string} [proof]
   * @param {string} [nonce]
   * @param {string} [founderPlayerId]
   * @param {string} [address]
   * @param {string} [proofPubKey]
   * @param {string} [proofSignature]
   * @param {object} [options]
   */
  async queueMsgGuildCreate(
    reactorId,
    endpoint,
    entrySubstationId,
    proof = '',
    nonce = '',
    founderPlayerId = '',
    address = '',
    proofPubKey = '',
    proofSignature = '',
    options = {}
  ) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildCreate',
      {
        reactorId,
        endpoint,
        entrySubstationId,
        proof,
        nonce,
        founderPlayerId,
        address,
        proofPubKey,
        proofSignature,
      },
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} guildId
   * @param {string} entrySubstationId
   * @param {object} [options]
   */
  async queueMsgGuildUpdateEntrySubstationId(guildId, entrySubstationId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildUpdateEntrySubstationId',
      {guildId, entrySubstationId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} guildId
   * @param {string} owner
   * @param {object} [options]
   */
  async queueMsgGuildUpdateOwnerId(guildId, owner, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildUpdateOwnerId',
      {guildId, owner},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} guildId
   * @param {string} endpoint
   * @param {object} [options]
   */
  async queueMsgGuildUpdateEndpoint(guildId, endpoint, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildUpdateEndpoint',
      {guildId, endpoint},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} guildId
   * @param {string} reactorId
   * @param {object} [options]
   */
  async queueMsgGuildUpdatePrimaryReactor(guildId, reactorId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildUpdatePrimaryReactor',
      {guildId, reactorId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * Sets the fee retained in collateral when converting ualpha into this
   * guild's token.
   *
   * @param {string} guildId
   * @param {string} bankConvertInFee Decimal string, 0.0 inclusive to 1.0 exclusive.
   * @param {object} [options]
   */
  async queueMsgGuildUpdateBankConvertInFee(guildId, bankConvertInFee, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildUpdateBankConvertInFee',
      {guildId, bankConvertInFee},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * Sets the fee retained in collateral when redeeming this guild's token for
   * ualpha.
   *
   * @param {string} guildId
   * @param {string} bankConvertOutFee Decimal string, 0.0 inclusive to 1.0 exclusive.
   * @param {object} [options]
   */
  async queueMsgGuildUpdateBankConvertOutFee(guildId, bankConvertOutFee, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildUpdateBankConvertOutFee',
      {guildId, bankConvertOutFee},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} guildId
   * @param {string} name
   * @param {object} [options]
   */
  async queueMsgGuildUpdateName(guildId, name, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildUpdateName',
      {guildId, name},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} guildId
   * @param {string} pfp
   * @param {object} [options]
   */
  async queueMsgGuildUpdatePfp(guildId, pfp, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildUpdatePfp',
      {guildId, pfp},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} guildId
   * @param {string|number} joinInfusionMinimum
   * @param {object} [options]
   */
  async queueMsgGuildUpdateJoinInfusionMinimum(guildId, joinInfusionMinimum, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildUpdateJoinInfusionMinimum',
      {guildId, joinInfusionMinimum},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} guildId
   * @param {number} guildJoinBypassLevel
   * @param {object} [options]
   */
  async queueMsgGuildUpdateJoinInfusionMinimumBypassByInvite(guildId, guildJoinBypassLevel, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildUpdateJoinInfusionMinimumBypassByInvite',
      {guildId, guildJoinBypassLevel},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} guildId
   * @param {number} guildJoinBypassLevel
   * @param {object} [options]
   */
  async queueMsgGuildUpdateJoinInfusionMinimumBypassByRequest(guildId, guildJoinBypassLevel, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildUpdateJoinInfusionMinimumBypassByRequest',
      {guildId, guildJoinBypassLevel},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string|number} newEntryRank
   * @param {object} [options]
   */
  async queueMsgGuildUpdateEntryRank(newEntryRank, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildUpdateEntryRank',
      {newEntryRank},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} guildId
   * @param {string} playerId
   * @param {string} substationId
   * @param {object} [options]
   */
  async queueMsgGuildMembershipInvite(guildId, playerId, substationId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildMembershipInvite',
      {guildId, playerId, substationId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} guildId
   * @param {string} playerId
   * @param {string} substationId
   * @param {object} [options]
   */
  async queueMsgGuildMembershipInviteApprove(guildId, playerId, substationId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildMembershipInviteApprove',
      {guildId, playerId, substationId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} guildId
   * @param {string} playerId
   * @param {object} [options]
   */
  async queueMsgGuildMembershipInviteDeny(guildId, playerId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildMembershipInviteDeny',
      {guildId, playerId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} guildId
   * @param {string} playerId
   * @param {object} [options]
   */
  async queueMsgGuildMembershipInviteRevoke(guildId, playerId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildMembershipInviteRevoke',
      {guildId, playerId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} guildId
   * @param {string} playerId
   * @param {string} substationId
   * @param {string[]} infusionId
   * @param {object} [options]
   */
  async queueMsgGuildMembershipJoin(guildId, playerId, substationId, infusionId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildMembershipJoin',
      {guildId, playerId, substationId, infusionId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} address
   * @param {string} substationId
   * @param {string} proofPubKey
   * @param {string} proofSignature
   * @param {string} playerName
   * @param {string} playerPfp
   * @param {string} playerPfpClientRenderAttributes
   * @param {object} [options]
   */
  async queueMsgGuildMembershipJoinProxy(address, substationId, proofPubKey, proofSignature, playerName, playerPfp, playerPfpClientRenderAttributes, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildMembershipJoinProxy',
      {address, substationId, proofPubKey, proofSignature, playerName, playerPfp, playerPfpClientRenderAttributes},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} guildId
   * @param {string} playerId
   * @param {object} [options]
   */
  async queueMsgGuildMembershipKick(guildId, playerId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildMembershipKick',
      {guildId, playerId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} guildId
   * @param {string} playerId
   * @param {string} substationId
   * @param {object} [options]
   */
  async queueMsgGuildMembershipRequest(guildId, playerId, substationId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildMembershipRequest',
      {guildId, playerId, substationId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} guildId
   * @param {string} playerId
   * @param {string} substationId
   * @param {object} [options]
   */
  async queueMsgGuildMembershipRequestApprove(guildId, playerId, substationId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildMembershipRequestApprove',
      {guildId, playerId, substationId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} guildId
   * @param {string} playerId
   * @param {object} [options]
   */
  async queueMsgGuildMembershipRequestDeny(guildId, playerId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildMembershipRequestDeny',
      {guildId, playerId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} guildId
   * @param {string} playerId
   * @param {object} [options]
   */
  async queueMsgGuildMembershipRequestRevoke(guildId, playerId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgGuildMembershipRequestRevoke',
      {guildId, playerId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} objectId
   * @param {string} playerId
   * @param {string|number} permissions
   * @param {object} [options]
   */
  async queueMsgPermissionGrantOnObject(objectId, playerId, permissions, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgPermissionGrantOnObject',
      {objectId, playerId, permissions},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} address
   * @param {string|number} permissions
   * @param {object} [options]
   */
  async queueMsgPermissionGrantOnAddress(address, permissions, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgPermissionGrantOnAddress',
      {address, permissions},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} objectId
   * @param {string} playerId
   * @param {string|number} permissions
   * @param {object} [options]
   */
  async queueMsgPermissionRevokeOnObject(objectId, playerId, permissions, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgPermissionRevokeOnObject',
      {objectId, playerId, permissions},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} address
   * @param {string|number} permissions
   * @param {object} [options]
   */
  async queueMsgPermissionRevokeOnAddress(address, permissions, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgPermissionRevokeOnAddress',
      {address, permissions},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} objectId
   * @param {string} playerId
   * @param {string|number} permissions
   * @param {object} [options]
   */
  async queueMsgPermissionSetOnObject(objectId, playerId, permissions, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgPermissionSetOnObject',
      {objectId, playerId, permissions},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} address
   * @param {string|number} permissions
   * @param {object} [options]
   */
  async queueMsgPermissionSetOnAddress(address, permissions, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgPermissionSetOnAddress',
      {address, permissions},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} objectId
   * @param {string} guildId
   * @param {string|number} permission
   * @param {string|number} rank
   * @param {object} [options]
   */
  async queueMsgPermissionGuildRankSet(objectId, guildId, permission, rank, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgPermissionGuildRankSet',
      {objectId, guildId, permission, rank},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} objectId
   * @param {string} guildId
   * @param {string|number} permission
   * @param {object} [options]
   */
  async queueMsgPermissionGuildRankRevoke(objectId, guildId, permission, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgPermissionGuildRankRevoke',
      {objectId, guildId, permission},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} primaryAddress
   * @param {object} [options]
   */
  async queueMsgPlayerUpdatePrimaryAddress(primaryAddress, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgPlayerUpdatePrimaryAddress',
      {primaryAddress},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} playerId
   * @param {string|number} guildRank
   * @param {object} [options]
   */
  async queueMsgPlayerUpdateGuildRank(playerId, guildRank, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgPlayerUpdateGuildRank',
      {playerId, guildRank},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} playerId
   * @param {string} name
   * @param {object} [options]
   */
  async queueMsgPlayerUpdateName(playerId, name, options = {}) {
    const id = this.queue.enqueueGameplayImmediate(
      '/structs.structs.MsgPlayerUpdateName',
      {playerId, name},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} playerId
   * @param {string} pfp
   * @param {object} [options]
   */
  async queueMsgPlayerUpdatePfp(playerId, pfp, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgPlayerUpdatePfp',
      {playerId, pfp},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} playerId
   * @param {string} pfpClientRenderAttributes
   * @param {object} [options]
   */
  async queueMsgPlayerUpdatePfpClientRenderAttributes(playerId, pfpClientRenderAttributes, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgPlayerUpdatePfpClientRenderAttributes',
      {playerId, pfpClientRenderAttributes},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} structId
   * @param {object} [options]
   */
  async queueMsgStructDeactivate(structId, options = {}) {
    const id = this.queue.enqueueGameplayAction(
      '/structs.structs.MsgStructDeactivate',
      {structId},
      0,
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string[]} structIds
   * @param {object} [options]
   */
  async queueMsgStructDeactivateBatch(structIds, options = {}) {
    const id = this.queue.enqueueAction(
      '/structs.structs.MsgStructDeactivateBatch',
      {structId: structIds},
      0,
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} structId
   * @param {number} chargeCost
   * @param {object} [options]
   */
  async queueMsgStructTrash(structId, chargeCost, options = {}) {
    const id = this.queue.enqueueAction(
      '/structs.structs.MsgStructTrash',
      {structId},
      chargeCost,
      options,
    );
    if (chargeCost > 0) {
      this.gameState.keyPlayers[PLAYER_TYPES.PLAYER]
        .setOptimisticLastActionBlockHeight(this.gameState.currentBlockHeight);
    }
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} structId
   * @param {object} [options]
   */
  async queueMsgStructBuildCancel(structId, options = {}) {
    const id = this.queue.enqueueGameplayImmediate(
      '/structs.structs.MsgStructBuildCancel',
      {structId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} structId
   * @param {string} infuseAmount
   * @param {object} [options]
   */
  async queueMsgStructGeneratorInfuse(structId, infuseAmount, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgStructGeneratorInfuse',
      {structId, infuseAmount},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} owner
   * @param {string} allocationId
   * @param {object} [options]
   */
  async queueMsgSubstationCreate(owner, allocationId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgSubstationCreate',
      {owner, allocationId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} substationId
   * @param {string} name
   * @param {object} [options]
   */
  async queueMsgSubstationUpdateName(substationId, name, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgSubstationUpdateName',
      {substationId, name},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} substationId
   * @param {string} pfp
   * @param {object} [options]
   */
  async queueMsgSubstationUpdatePfp(substationId, pfp, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgSubstationUpdatePfp',
      {substationId, pfp},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} substationId
   * @param {string} migrationSubstationId
   * @param {object} [options]
   */
  async queueMsgSubstationDelete(substationId, migrationSubstationId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgSubstationDelete',
      {substationId, migrationSubstationId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} allocationId
   * @param {string} destinationId
   * @param {object} [options]
   */
  async queueMsgSubstationAllocationConnect(allocationId, destinationId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgSubstationAllocationConnect',
      {allocationId, destinationId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} allocationId
   * @param {object} [options]
   */
  async queueMsgSubstationAllocationDisconnect(allocationId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgSubstationAllocationDisconnect',
      {allocationId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} substationId
   * @param {string} playerId
   * @param {object} [options]
   */
  async queueMsgSubstationPlayerConnect(substationId, playerId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgSubstationPlayerConnect',
      {substationId, playerId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} playerId
   * @param {object} [options]
   */
  async queueMsgSubstationPlayerDisconnect(playerId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgSubstationPlayerDisconnect',
      {playerId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} substationId
   * @param {string[]} playerId
   * @param {object} [options]
   */
  async queueMsgSubstationPlayerMigrate(substationId, playerId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgSubstationPlayerMigrate',
      {substationId, playerId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} providerId
   * @param {string|number} duration
   * @param {string|number} capacity
   * @param {object} [options]
   * @param {string} [options.ownerId] Explicit v0.22 vault owner; empty preserves the retained-address/default selector.
   */
  async queueMsgAgreementOpen(providerId, duration, capacity, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgAgreementOpen',
      {providerId, duration, capacity, ownerId: options.ownerId ?? ''},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} agreementId
   * @param {object} [options]
   */
  async queueMsgAgreementClose(agreementId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgAgreementClose',
      {agreementId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} agreementId
   * @param {string|number} capacityIncrease
   * @param {object} [options]
   */
  async queueMsgAgreementCapacityIncrease(agreementId, capacityIncrease, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgAgreementCapacityIncrease',
      {agreementId, capacityIncrease},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} agreementId
   * @param {string|number} capacityDecrease
   * @param {object} [options]
   */
  async queueMsgAgreementCapacityDecrease(agreementId, capacityDecrease, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgAgreementCapacityDecrease',
      {agreementId, capacityDecrease},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} agreementId
   * @param {string|number} durationIncrease
   * @param {object} [options]
   */
  async queueMsgAgreementDurationIncrease(agreementId, durationIncrease, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgAgreementDurationIncrease',
      {agreementId, durationIncrease},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} substationId
   * @param {{denom: string, amount: string}} rate
   * @param {string} accessPolicy
   * @param {string} providerCancellationPenalty
   * @param {string} consumerCancellationPenalty
   * @param {string|number} capacityMinimum
   * @param {string|number} capacityMaximum
   * @param {string|number} durationMinimum
   * @param {string|number} durationMaximum
   * @param {object} [options]
   * @param {string} [options.ownerId] Explicit v0.22 vault owner; empty preserves the retained-address/default selector.
   */
  async queueMsgProviderCreate(substationId, rate, accessPolicy, providerCancellationPenalty, consumerCancellationPenalty, capacityMinimum, capacityMaximum, durationMinimum, durationMaximum, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgProviderCreate',
      {
        substationId,
        rate,
        accessPolicy,
        providerCancellationPenalty,
        consumerCancellationPenalty,
        capacityMinimum,
        capacityMaximum,
        durationMinimum,
        durationMaximum,
        ownerId: options.ownerId ?? '',
      },
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} providerId
   * @param {string} destinationAddress
   * @param {object} [options]
   */
  async queueMsgProviderWithdrawBalance(providerId, destinationAddress, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgProviderWithdrawBalance',
      {providerId, destinationAddress},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} providerId
   * @param {string|number} newMinimumCapacity
   * @param {object} [options]
   */
  async queueMsgProviderUpdateCapacityMinimum(providerId, newMinimumCapacity, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgProviderUpdateCapacityMinimum',
      {providerId, newMinimumCapacity},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} providerId
   * @param {string|number} newMaximumCapacity
   * @param {object} [options]
   */
  async queueMsgProviderUpdateCapacityMaximum(providerId, newMaximumCapacity, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgProviderUpdateCapacityMaximum',
      {providerId, newMaximumCapacity},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} providerId
   * @param {string|number} newMinimumDuration
   * @param {object} [options]
   */
  async queueMsgProviderUpdateDurationMinimum(providerId, newMinimumDuration, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgProviderUpdateDurationMinimum',
      {providerId, newMinimumDuration},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} providerId
   * @param {string|number} newMaximumDuration
   * @param {object} [options]
   */
  async queueMsgProviderUpdateDurationMaximum(providerId, newMaximumDuration, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgProviderUpdateDurationMaximum',
      {providerId, newMaximumDuration},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} providerId
   * @param {string} accessPolicy
   * @param {object} [options]
   */
  async queueMsgProviderUpdateAccessPolicy(providerId, accessPolicy, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgProviderUpdateAccessPolicy',
      {providerId, accessPolicy},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} providerId
   * @param {object} [options]
   */
  async queueMsgProviderDelete(providerId, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgProviderDelete',
      {providerId},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} delegatorAddress
   * @param {string} validatorAddress
   * @param {{denom: string, amount: string}} amount
   * @param {object} [options]
   * @param {string} [options.ownerId] Explicit v0.22 vault owner; empty preserves the retained-address/default selector.
   */
  async queueMsgReactorInfuse(delegatorAddress, validatorAddress, amount, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgReactorInfuse',
      {delegatorAddress, validatorAddress, amount, ownerId: options.ownerId ?? ''},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} delegatorAddress
   * @param {string} validatorSrcAddress
   * @param {string} validatorDstAddress
   * @param {{denom: string, amount: string}} amount
   * @param {object} [options]
   * @param {string} [options.ownerId] Explicit v0.22 vault owner; empty preserves the retained-address/default selector.
   */
  async queueMsgReactorBeginMigration(delegatorAddress, validatorSrcAddress, validatorDstAddress, amount, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgReactorBeginMigration',
      {delegatorAddress, validatorSrcAddress, validatorDstAddress, amount, ownerId: options.ownerId ?? ''},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} delegatorAddress
   * @param {string} validatorAddress
   * @param {{denom: string, amount: string}} amount
   * @param {object} [options]
   * @param {string} [options.ownerId] Explicit v0.22 vault owner; empty preserves the retained-address/default selector.
   */
  async queueMsgReactorDefuse(delegatorAddress, validatorAddress, amount, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgReactorDefuse',
      {delegatorAddress, validatorAddress, amount, ownerId: options.ownerId ?? ''},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} delegatorAddress
   * @param {string} validatorAddress
   * @param {{denom: string, amount: string}} amount
   * @param {string|number} creationHeight
   * @param {object} [options]
   * @param {string} [options.ownerId] Explicit v0.22 vault owner; empty preserves the retained-address/default selector.
   */
  async queueMsgReactorCancelDefusion(delegatorAddress, validatorAddress, amount, creationHeight, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgReactorCancelDefusion',
      {delegatorAddress, validatorAddress, amount, creationHeight, ownerId: options.ownerId ?? ''},
      options,
    );
    return this.queue.whenSettled(id);
  }

  /**
   * Restores energy production for a reactor whose validator was jailed and
   * then unjailed below the active-set cutoff, where staking never rebonds it.
   * Permissionless: it only writes state derived from the staking module.
   *
   * @param {string} validatorAddress
   * @param {object} [options]
   */
  async queueMsgReactorRestart(validatorAddress, options = {}) {
    const id = this.queue.enqueueImmediate(
      '/structs.structs.MsgReactorRestart',
      {validatorAddress},
      options,
    );
    return this.queue.whenSettled(id);
  }

  // ---------------------------------------------------------------------------
  // Charge-lane messages (broadcast gated until on-chain charge is sufficient)
  //
  // chargeCost is caller-supplied. When chargeCost > 0 we optimistically drain
  // the DISPLAY charge bar (setOptimisticLastActionBlockHeight) for immediate UI
  // feedback; this never touches confirmedLastActionBlockHeight, so the queue
  // scheduler still broadcasts as soon as on-chain charge allows. GRASS
  // (KeyPlayerLastActionListener) remains the sole writer of confirmed lastAction.
  // ---------------------------------------------------------------------------

  /**
   * @param {string} structId
   * @param {number} chargeCost
   * @param {object} [options]
   */
  async queueMsgStructActivate(structId, chargeCost, options = {}) {
    const id = this.queue.enqueueGameplayAction(
      '/structs.structs.MsgStructActivate',
      {structId},
      chargeCost,
      options,
    );
    if (chargeCost > 0) {
      this.gameState.keyPlayers[PLAYER_TYPES.PLAYER]
        .setOptimisticLastActionBlockHeight(this.gameState.currentBlockHeight);
    }
    return this.queue.whenSettled(id);
  }

  /**
   * Enqueue is synchronous: the build control must not show a pending slot if
   * persistence or submission recovery refuses it. Settlement remains a Promise.
   *
   * @param {string} playerId
   * @param {string|number} structTypeId
   * @param {string} operatingAmbit
   * @param {string|number} slot
   * @param {number} chargeCost
   * @param {object} [options]
   */
  queueMsgStructBuildInitiate(playerId, structTypeId, operatingAmbit, slot, chargeCost, options = {}) {
    const operatingAmbitIndex = AMBIT_ENUM[operatingAmbit.toUpperCase()];
    const id = this.queue.enqueueGameplayAction(
      '/structs.structs.MsgStructBuildInitiate',
      {playerId, structTypeId, operatingAmbit: operatingAmbitIndex, slot},
      chargeCost,
      options,
    );
    if (chargeCost > 0) {
      this.gameState.keyPlayers[PLAYER_TYPES.PLAYER]
        .setOptimisticLastActionBlockHeight(this.gameState.currentBlockHeight);
    }
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} defenderStructId
   * @param {string} protectedStructId
   * @param {number} chargeCost
   * @param {object} [options]
   */
  async queueMsgStructDefenseSet(defenderStructId, protectedStructId, chargeCost, options = {}) {
    const id = this.queue.enqueueGameplayAction(
      '/structs.structs.MsgStructDefenseSet',
      {defenderStructId, protectedStructId},
      chargeCost,
      options,
    );
    if (chargeCost > 0) {
      this.gameState.keyPlayers[PLAYER_TYPES.PLAYER]
        .setOptimisticLastActionBlockHeight(this.gameState.currentBlockHeight);
    }
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} defenderStructId
   * @param {number} chargeCost
   * @param {object} [options]
   */
  async queueMsgStructDefenseClear(defenderStructId, chargeCost, options = {}) {
    const id = this.queue.enqueueGameplayAction(
      '/structs.structs.MsgStructDefenseClear',
      {defenderStructId},
      chargeCost,
      options,
    );
    if (chargeCost > 0) {
      this.gameState.keyPlayers[PLAYER_TYPES.PLAYER]
        .setOptimisticLastActionBlockHeight(this.gameState.currentBlockHeight);
    }
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} structId
   * @param {string} locationType
   * @param {string} ambit
   * @param {string|number} slot
   * @param {number} chargeCost
   * @param {object} [options]
   */
  async queueMsgStructMove(structId, locationType, ambit, slot, chargeCost, options = {}) {
    const locationTypeIndex = LOCATION_TYPE_INDEX[locationType.toLowerCase()];
    const ambitNumber = AMBIT_ENUM[ambit.toUpperCase()];
    const id = this.queue.enqueueGameplayAction(
      '/structs.structs.MsgStructMove',
      {structId, locationType: locationTypeIndex, ambit: ambitNumber, slot},
      chargeCost,
      options,
    );
    if (chargeCost > 0) {
      this.gameState.keyPlayers[PLAYER_TYPES.PLAYER]
        .setOptimisticLastActionBlockHeight(this.gameState.currentBlockHeight);
    }
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} operatingStructId
   * @param {string[]} targetStructId
   * @param {string} weaponSystem
   * @param {number} chargeCost
   * @param {object} [options]
   */
  async queueMsgStructAttack(operatingStructId, targetStructId, weaponSystem, chargeCost, options = {}) {
    const id = this.queue.enqueueGameplayAction(
      '/structs.structs.MsgStructAttack',
      {operatingStructId, targetStructId, weaponSystem},
      chargeCost,
      options,
    );
    if (chargeCost > 0) {
      this.gameState.keyPlayers[PLAYER_TYPES.PLAYER]
        .setOptimisticLastActionBlockHeight(this.gameState.currentBlockHeight);
    }
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} structId
   * @param {number} chargeCost
   * @param {object} [options]
   */
  async queueMsgStructStealthActivate(structId, chargeCost, options = {}) {
    const id = this.queue.enqueueGameplayAction(
      '/structs.structs.MsgStructStealthActivate',
      {structId},
      chargeCost,
      options,
    );
    if (chargeCost > 0) {
      this.gameState.keyPlayers[PLAYER_TYPES.PLAYER]
        .setOptimisticLastActionBlockHeight(this.gameState.currentBlockHeight);
    }
    return this.queue.whenSettled(id);
  }

  /**
   * @param {string} structId
   * @param {number} chargeCost
   * @param {object} [options]
   */
  async queueMsgStructStealthDeactivate(structId, chargeCost, options = {}) {
    const id = this.queue.enqueueGameplayAction(
      '/structs.structs.MsgStructStealthDeactivate',
      {structId},
      chargeCost,
      options,
    );
    if (chargeCost > 0) {
      this.gameState.keyPlayers[PLAYER_TYPES.PLAYER]
        .setOptimisticLastActionBlockHeight(this.gameState.currentBlockHeight);
    }
    return this.queue.whenSettled(id);
  }

}
